import { httpCoreService } from "../services/HttpCoreService";
import type { ApiResponse } from "../types/auth.type";
import type {
  VisitAttachment,
  VisitAttachmentListResponse,
  VisitAttachmentStats,
  VisitAttachmentDownloadUrl,
} from "../pages/MonitoringTracker/types/monitoringVisit.types";

export interface VisitAttachmentListParams {
  visit_id?: string;
  uploaded_by?: string;
  includeInactive?: boolean;
  page?: number;
  limit?: number;
}

export interface IVisitAttachmentRepository {
  upload(visitId: string, file: File): Promise<VisitAttachment>;
  list(
    params?: VisitAttachmentListParams,
  ): Promise<VisitAttachmentListResponse>;
  getByVisit(visitId: string): Promise<VisitAttachment[]>;
  getStats(visitId: string): Promise<VisitAttachmentStats>;
  getById(id: string): Promise<VisitAttachment>;
  delete(id: string): Promise<void>;
  getDownloadUrl(
    id: string,
    expiresIn?: number,
  ): Promise<VisitAttachmentDownloadUrl>;
}

/** Map API response (snake_case or camelCase) to VisitAttachment */
function mapAttachment(raw: Record<string, unknown>): VisitAttachment {
  return {
    id: (raw.id as string) ?? "",
    visitId: (raw.visitId as string) ?? (raw.visit_id as string) ?? "",
    originalName:
      (raw.originalName as string) ?? (raw.original_name as string) ?? "",
    mimeType: (raw.mimeType as string) ?? (raw.mime_type as string) ?? "",
    fileSize: Number(raw.fileSize ?? raw.file_size ?? 0),
    createdAt: (raw.createdAt as string) ?? (raw.created_at as string) ?? "",
    uploader: raw.uploader as { id: string; email: string } | undefined,
  };
}

export class VisitAttachmentRepository implements IVisitAttachmentRepository {
  async upload(visitId: string, file: File): Promise<VisitAttachment> {
    const formData = new FormData();
    formData.append("file", file);

    const response: ApiResponse<Record<string, unknown>> =
      await httpCoreService.postForm<Record<string, unknown>>(
        `/visit-attachments/upload/${visitId}`,
        formData,
      );

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to upload file",
      );
    }

    if (!response.data) {
      throw new Error("Upload succeeded but no data returned");
    }

    return mapAttachment(response.data);
  }

  async list(
    params?: VisitAttachmentListParams,
  ): Promise<VisitAttachmentListResponse> {
    const query: Record<string, string | number | boolean> = {};
    if (params?.visit_id) {
      query.visit_id = params.visit_id;
    }
    if (params?.uploaded_by) {
      query.uploaded_by = params.uploaded_by;
    }
    if (params?.includeInactive !== undefined) {
      query.includeInactive = params.includeInactive;
    }
    if (params?.page !== undefined) {
      query.page = params.page;
    }
    if (params?.limit !== undefined) {
      query.limit = params.limit;
    }

    const response: ApiResponse<{
      items?: unknown[];
      total?: number;
      page?: number;
      limit?: number;
    }> = await httpCoreService.get("/visit-attachments", { params: query });

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to list attachments",
      );
    }

    const data = response.data ?? {};
    const items = Array.isArray(data.items) ? data.items : [];
    return {
      items: items.map((i) => mapAttachment(i as Record<string, unknown>)),
      total: Number(data.total ?? 0),
      page: Number(data.page ?? 1),
      limit: Number(data.limit ?? 20),
    };
  }

  async getByVisit(visitId: string): Promise<VisitAttachment[]> {
    const response: ApiResponse<unknown> = await httpCoreService.get(
      `/visit-attachments/visit/${visitId}`,
    );

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to fetch attachments for visit",
      );
    }

    const items = Array.isArray(response.data) ? response.data : [];
    return items.map((i) => mapAttachment(i as Record<string, unknown>));
  }

  async getStats(visitId: string): Promise<VisitAttachmentStats> {
    const response: ApiResponse<{ count?: number; totalSize?: number }> =
      await httpCoreService.get(`/visit-attachments/visit/${visitId}/stats`);

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to fetch attachment stats",
      );
    }

    const data = (response.data ?? {}) as {
      count?: number;
      totalSize?: number;
      total_size?: number;
    };
    return {
      count: Number(data.count ?? 0),
      totalSize: Number(data.totalSize ?? data.total_size ?? 0),
    };
  }

  async getById(id: string): Promise<VisitAttachment> {
    const response: ApiResponse<Record<string, unknown>> =
      await httpCoreService.get(`/visit-attachments/${id}`);

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to fetch attachment",
      );
    }

    if (!response.data) {
      throw new Error("Attachment not found");
    }

    return mapAttachment(response.data);
  }

  async delete(id: string): Promise<void> {
    const response = await httpCoreService.delete(`/visit-attachments/${id}`);

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to delete attachment",
      );
    }
  }

  async getDownloadUrl(
    id: string,
    expiresIn = 3600,
  ): Promise<VisitAttachmentDownloadUrl> {
    const response: ApiResponse<{
      data?: { url?: string; expiresIn?: number };
    }> = await httpCoreService.get(`/visit-attachments/${id}/download-url`, {
      params: { expiresIn },
    });

    const ok = response.statusCode >= 200 && response.statusCode < 300;
    if (!ok) {
      throw new Error(
        (response as { error?: string; message?: string }).error ??
          (response as { message?: string }).message ??
          "Failed to get download URL",
      );
    }

    const payload = response.data?.data ?? response.data;
    const raw =
      typeof payload === "object" && payload !== null
        ? (payload as {
            url?: string;
            expiresIn?: number;
            expires_in?: number;
          })
        : {};
    return {
      url: raw.url ?? "",
      expiresIn: Number(raw.expiresIn ?? raw.expires_in ?? 3600),
    };
  }
}
